import type { Customer, Item, Part, Site } from '../../domain/inventory';
import { statusLabels } from '../../domain/status';
import { siteLabel } from '../../lib/site-label';

interface ExportDialogProps {
  items: Item[];
  partsById: Map<string, Part>;
  sitesById: Map<string, Site>;
  customersById: Map<string, Customer>;
  onClose: () => void;
}

const EXPORT_LIMIT = 5000;

function csvCell(value: string | number | boolean | undefined | null): string {
  const text = value === undefined || value === null ? '' : String(value);
  return `"${text.replaceAll('"', '""')}"`;
}

export function ExportDialog({
  items,
  partsById,
  sitesById,
  customersById,
  onClose,
}: ExportDialogProps) {
  const exportItems = items.slice(0, EXPORT_LIMIT);

  function download(): void {
    const headings = [
      'item_id',
      'part_id',
      'part_type',
      'build_stage',
      'hardware_revision',
      'customer',
      'assigned_site',
      'current_location_site',
      'location_detail',
      'status',
      'status_label',
      'needs_attention',
      'attention_reason',
      'last_inspection',
      'last_maintenance',
      'last_modified',
      'modified_by',
      'version',
    ];
    const lines = exportItems.map((item) => {
      const part = partsById.get(item.partId);
      const assignedSite = sitesById.get(item.siteId);
      const locationSite = sitesById.get(item.locationSiteId ?? item.siteId);
      const customer = customersById.get(assignedSite?.customerId ?? '');
      return [
        item.id,
        item.partId,
        part?.type,
        part?.build,
        item.hardwareRevision,
        customer?.displayName ?? customer?.name,
        siteLabel(assignedSite, item.siteId),
        siteLabel(locationSite, item.locationSiteId ?? item.siteId),
        item.location,
        item.status ?? 'STATUS_UNSPECIFIED',
        statusLabels[item.status ?? 'STATUS_UNSPECIFIED'],
        Boolean(item.needsAttention),
        item.attentionReason,
        item.inspectedDate,
        item.maintenanceDate,
        item.lastModified,
        item.lastModifiedBy,
        item.version,
      ]
        .map(csvCell)
        .join(',');
    });
    const csv = [headings.map(csvCell).join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `leatherback-inventory-${new Date().toISOString().slice(0, 10)}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
    onClose();
  }

  return (
    <div className="drawer-overlay centered-overlay" role="presentation" onClick={onClose}>
      <div
        className="confirmation-dialog"
        role="dialog"
        aria-label="Review inventory export"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="drawer-heading">
          <div>
            <h2>Export items</h2>
          </div>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Close export review"
          >
            ×
          </button>
        </div>
        <p>
          <strong>{exportItems.length.toLocaleString()}</strong> filtered records.
        </p>
        <dl className="review-grid">
          <dt>Format</dt>
          <dd>CSV, UTF-8</dd>
          <dt>Maximum</dt>
          <dd>{EXPORT_LIMIT.toLocaleString()} records</dd>
          <dt>Scope</dt>
          <dd>Current filters and sort order</dd>
        </dl>
        <div className="dialog-actions">
          <button type="button" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="primary"
            onClick={download}
            disabled={exportItems.length === 0}
          >
            Download CSV
          </button>
        </div>
      </div>
    </div>
  );
}
